import React from "react";
import { useState, useEffect } from "react";
import { useSelector, useDispatch } from "types/store";
import styles from "./style.module.css";

import { Button } from "aible";

import { toast } from "react-toastify";
import { isEnabled } from "utils/featureFlags";

import * as chatGPTReducer from "containers/ChatGPT/reducer";
import * as blueprintReducer from "containers/Blueprint/reducer";
import * as languageModelArtifactReducer from "containers/LanguageModelArtifact/reducer";
import * as homeReducer from "containers/Home/reducer";
import * as accountsReducer from "containers/Accounts/reducer";
import * as chatAppReducer from "containers/ChatApp/reducer";

import AccountSelector from "containers/Accounts/AccountSelector";

import { LineInput } from "components/Inputs/LineInput";
import LoadingIndicator from "components/LoadingIndicator/LoadingIndicator";

import FolderUserSelect from "components/Folders/FolderUserSelect";

import * as foldersReducer from "containers/Folders/reducer";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/aible/Dialog/Dialog";

const ShareChatApp = (props) => {
  const { id, showModal, updateShowModal, hideButton, onClick, ...retProps } = props;
  const [showShareModal, updateShowShareModal] = useState(showModal);
  useEffect(() => {
    handleUpdateShowShareModal(showModal);
  }, [showModal]);

  const handleUpdateShowShareModal = (val) => {
    if (updateShowModal && val !== showModal) {
      updateShowModal(val);
    }
    updateShowShareModal(val);
  };

  return (
    <React.Fragment>
      {!hideButton && (
        <Button
          type={"secondary"}
          onClick={(e) => {
            if (onClick) {
              onClick(e);
            }
            handleUpdateShowShareModal(true);
          }}
          {...retProps}
        >
          {props.children}
        </Button>
      )}
      <ShareChatAppModal id={id} showModal={showShareModal} updateShowModal={handleUpdateShowShareModal} />
    </React.Fragment>
  );
};

const ShareChatAppModal = ({ id, updateShowModal, showModal } = {}) => {
  const dispatch = useDispatch();

  const chatApp = useSelector((state) => blueprintReducer.getBlueprint(state, id));

  const [shareLink, updateShareLink] = useState(undefined);
  const [isGeneratingLink, updateIsGeneratingLink] = useState(false);

  let chatAppAccount = useSelector((state) => chatGPTReducer.getChatGPTAccountId(state));

  const chatAppArtifactId = chatApp?.wizard?.steps?.[0]?.artifactId;
  const artifact = useSelector((state) => languageModelArtifactReducer.getCorpus(state, chatAppArtifactId));

  const chatAppDatasetId = chatApp?.wizard?.steps?.[0]?.datasetId;
  const dataset = useSelector((state) => homeReducer.getDataset(state, chatAppDatasetId));

  if (chatAppArtifactId) {
    chatAppAccount = artifact?.account_id;
  } else if (chatAppDatasetId) {
    chatAppAccount = dataset?.account_id;
  }

  const hasChatAppAccount = Boolean(chatAppArtifactId || chatAppDatasetId);

  const accounts = useSelector((state) => accountsReducer.getActiveAccounts(state));

  const folderId = useSelector((state) => foldersReducer.getSelectedFolder(state));
  const canManagePermissions = useSelector((state) => foldersReducer.getUserManagePermissions(state, folderId));

  const [email, updateEmail] = useState("");
  const [isInviting, updateIsInviting] = useState(false);

  const [shareUser, updateShareUser] = useState();
  const [sharePassword, updateSharePassword] = useState("");
  const [shareAccount, updateShareAccount] = useState(chatAppAccount);
  const [shareError, updateShareError] = useState(undefined);

  const shareChat = () => dispatch(chatAppReducer.shareChatAppByEmail(id, email));

  const shareChatAppByLink = (email, password, accountId) =>
    dispatch(chatAppReducer.shareChatAppByLink(id, email, password, accountId));

  const handleReset = () => {
    updateIsGeneratingLink(false);
    updateShareLink(undefined);
    updateShareError(undefined);
  };

  const handleCloseModal = () => {
    updateShowModal(false);
    handleReset();
  };

  const generateLink = () => {
    updateIsGeneratingLink(true);
    updateShareError(undefined);
    /* GENERATE LINK HERE */
    shareChatAppByLink(shareUser?.email, sharePassword, shareAccount)
      .then((url) => {
        updateShareLink(url);
        updateIsGeneratingLink(false);
      })
      .catch((e) => {
        updateShareLink(undefined);
        updateIsGeneratingLink(false);

        let errorMessage = "Something went wrong. Please try again.";
        if (typeof e?.message === "string") {
          errorMessage = e?.message;
        } else if (Array.isArray(e?.message)) {
          errorMessage = e.message[0]?.message;
        } else {
          errorMessage = JSON.stringify(e?.message);
        }
        updateShareError(errorMessage);
      });
  };

  const handleCopyShareLink = () => {
    if (!shareLink) {
      toast(`No link found, please try again.`, {
        type: toast.TYPE.ERROR,
        position: toast.POSITION.TOP_CENTER,
        autoClose: 3500
      });
      return;
    }
    toast(`Sharing link for ${chatApp?.name} App copied to clipboard.`, {
      type: toast.TYPE.SUCCESS,
      position: toast.POSITION.TOP_CENTER,
      autoClose: 3500
    });
    navigator.clipboard.writeText(shareLink);
  };

  return (
    <Dialog open={showModal} onOpenChange={() => handleCloseModal()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{`Share ${chatApp?.name ? `"${chatApp?.name}"` : "Chat"} App`}</DialogTitle>
        </DialogHeader>
        <div>
          <div className={styles.modalItem}>
            <div className={styles.modalLabel}>{"Invite By Email"}</div>
            <div className={styles.modalSubText}>
              {"Users without an Aible account will be invited to create an account."}
            </div>
            <div className={styles.modalSubText}>
              {
                "Aible users without access to this folder will be added as owners, allowing them to see all projects and datasets in the folder and invite other users."
              }
            </div>
            {!canManagePermissions && (
              <div className={styles.modalSubtext} style={{ color: "red", marginBottom: "8px" }}>
                {
                  "You do not have permission to invite users to this folder. Please contact the owner of the folder to share or have your permissions updated to owner in order to share."
                }
              </div>
            )}
            <div className={styles.modalContent}>
              <LineInput
                isBox
                placeholder={"Enter User Email"}
                value={email}
                className={styles.modalInput}
                onChange={(val) => updateEmail(val)}
                inputStyle={{ width: "100%" }}
                isDisabled={!canManagePermissions}
              />
              <Button
                type={"secondary"}
                onClick={() => {
                  updateIsInviting(true);
                  return shareChat()
                    .then(() => {
                      updateEmail("");
                      updateIsInviting(false);
                    })
                    .catch(() => {
                      updateEmail("");
                      updateIsInviting(false);
                    });
                }}
                isLoading={isInviting}
                disabled={!canManagePermissions}
              >
                {"Invite User"}
              </Button>
            </div>
          </div>
          {isEnabled("chat-aible-share-by-link") && (
            <div className={styles.modalItem} style={{ marginTop: "20px" }}>
              <div className={styles.modalLabel}>{"Create A Public URL"}</div>
              <div className={styles.modalSubText}>
                <div>{"Anyone on the internet with the created link will be able to access."}</div>
                <div>{"Login below to authenticate the user creating this public link."}</div>
                <div>{"The user and password will not be accessible to anyone using the link."}</div>
              </div>
              {isGeneratingLink ? (
                <div className={styles.modalContent}>
                  <span>Generating Link...</span>
                  <LoadingIndicator
                    style={{
                      width: "20px",
                      height: "18px",
                      transform: "scale(0.40)",
                      margin: "10px 0px 3px 0px"
                    }}
                  />
                </div>
              ) : shareLink ? (
                <div className={styles.modalContent}>
                  <LineInput
                    isBox
                    value={shareLink}
                    className={styles.modalInput}
                    inputStyle={{ width: "100%" }}
                    isDisabled
                  />
                  <div className={styles.modalInputWrapper}>
                    <Button
                      type={"secondary"}
                      onClick={() => {
                        handleCopyShareLink();
                      }}
                    >
                      {"Copy to Clipboard"}
                    </Button>
                    <Button
                      type={"secondary"}
                      style={{ marginLeft: "auto" }}
                      onClick={() => {
                        handleReset();
                      }}
                    >
                      {"Generate New Link"}
                    </Button>
                  </div>
                </div>
              ) : (
                <div className={styles.modalContent}>
                  <div className={styles.modalInputWrapper}>
                    <div className={styles.modalInputLabel}>{"Integration User"}</div>
                    <div className={styles.modalInputSelect} style={{ minHeight: "54px" }}>
                      <div style={{ width: "100%" }}>
                        <FolderUserSelect
                          value={shareUser?.id}
                          onChange={(v) => {
                            updateShareUser(v);
                          }}
                        />
                      </div>
                    </div>
                  </div>
                  <div className={styles.modalInputWrapper}>
                    <div className={styles.modalInputLabel}>{"Password"}</div>
                    <div className={styles.modalInputSelect}>
                      <LineInput
                        isBox
                        value={sharePassword}
                        onChange={updateSharePassword}
                        className={styles.modalInput}
                        inputStyle={{ width: "100%" }}
                        isPassword
                      />
                    </div>
                  </div>

                  <div className={styles.modalInputWrapper}>
                    {accounts?.length > 1 && (
                      <AccountSelector
                        value={shareAccount}
                        onChange={(val) => updateShareAccount(val?.id)}
                        isLocked={hasChatAppAccount ? true : undefined}
                      />
                    )}
                    <Button
                      type={"primary"}
                      onClick={() => {
                        generateLink();
                      }}
                      style={{ marginLeft: accounts?.length > 1 ? "auto" : undefined }}
                      isDisabled={!shareUser || !sharePassword}
                    >
                      {"Generate URL"}
                    </Button>
                  </div>
                  <div className={styles.modalInputWrapper}>
                    <div>{shareError}</div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button type="secondary" onClick={() => handleCloseModal()}>
            {"Close"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ShareChatApp;
